import clsx from 'clsx';
import React from 'react';

import { isEmpty, isValidNumber } from '../../../utils/utils';

import styles from './Input.module.scss';

type InputProps = {
    readonly placeholder?: string;
    readonly type?: string;
    readonly pattern?: string;
    readonly isValid?: boolean;
    readonly errorMessage?: string;
    readonly convertedText?: string;
    readonly value: string | number;
    readonly style?: React.CSSProperties;
    readonly inputStyle?: React.CSSProperties;
    readonly labelStyle?: React.CSSProperties;
    readonly unitContainer?: React.CSSProperties;
    readonly unitStyle?: React.CSSProperties;
    readonly onChange: Function;
    readonly name?: string;
    /** Forwarded to the native control so a sibling <label htmlFor> can name it. */
    readonly id?: string;
    readonly onEnter?: Function;
    readonly disabled?: boolean;
    readonly readOnly?: boolean;
    readonly maxLength?: number;
    readonly unit?: string | React.ReactNode;
    readonly textAreaRows?: number;
    readonly allowNegative?: boolean;
    readonly allowDecimal?: boolean;
    readonly numberOfDecimals?: number;
    readonly allowZero?: boolean;
    readonly allowAllCharacters?: boolean;
    readonly onClick?: () => void;
    readonly isActive?: boolean;
    readonly showFloatingLabel?: boolean;
    readonly onFocusProp?: () => void;
    readonly onBlurProp?: () => void;
    readonly autoComplete?: string;
    /**
     * Extra CSS class on the outermost container div that wraps the input, label, unit, and error elements.
     * Use this to override the container's width, border, background, or spacing from outside the component.
     */
    readonly containerClassName?: string;
    /** Extra CSS class applied directly to the `<input>` or `<textarea>` element (e.g. for text colour or font). */
    readonly inputClassName?: string;
    /**
     * Extra CSS class on the div that wraps the unit element (sits on the right side of the input).
     * Use this to change the unit area's border, background, or shadow — e.g. highlight it when the
     * input is in an "active / applied" state (purple border around the calendar icon area).
     */
    readonly unitContainerClassName?: string;
    /**
     * Extra CSS class applied to the inner unit content element (the icon or text node itself).
     * Use this to change the icon's colour, size, or opacity — e.g. tint the icon purple when
     * a custom filter is active.
     */
    readonly unitClassName?: string;
};

function Input({
    type = 'text',
    placeholder = '',
    pattern = '',
    isValid = true,
    errorMessage = '',
    convertedText = '',
    value = '',
    onChange,
    name,
    id,
    onEnter,
    style,
    inputStyle,
    labelStyle,
    unitContainer,
    unitStyle,
    disabled = false,
    readOnly = false,
    maxLength,
    unit,
    textAreaRows = 4,
    allowNegative = false,
    allowDecimal = false,
    numberOfDecimals = 4,
    allowZero = true,
    allowAllCharacters = false,
    onClick,
    isActive = false,
    showFloatingLabel = true,
    onFocusProp,
    onBlurProp,
    autoComplete,
    containerClassName,
    inputClassName,
    unitContainerClassName,
    unitClassName,
}: InputProps) {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let newValue = e.target.value;
        if (pattern && !e.target.validity.valid) return;
        if (maxLength && newValue?.length > maxLength) return;

        // If allowAllCharacters is true, skip all number-related validations
        if (!allowAllCharacters) {
            // For number inputs, strip non-numeric characters (except . and -)
            const isNumberInput = type?.includes('number') || allowDecimal || allowNegative;
            if (isNumberInput) {
                newValue = newValue.replace(/[^\d.-]/g, '');
                // Clear the value if it's just '.' or '-.' as these are not valid standalone values
                const invalidStandaloneValues = ['.', '-.'];
                if (invalidStandaloneValues.includes(newValue)) newValue = '';
                if (!isValidNumber(newValue)) return;
            }

            if (!allowNegative) newValue = newValue.replace(/-/g, '');
            if (!allowZero && newValue.replace('-', '') === '0') newValue = '';
            if (allowDecimal) {
                const decimalIndex = newValue.split('.')[1];
                if (decimalIndex && decimalIndex.length > numberOfDecimals) {
                    newValue = newValue.slice(0, newValue.indexOf('.') + numberOfDecimals + 1);
                }
            }
        }
        onChange(newValue);
    };

    const sanitizeValue = (
        val: string | number,
        type: string,
        allowDecimal: boolean,
        allowNegative: boolean,
        allowZero: boolean,
        allowAllCharacters: boolean,
    ): string | number => {
        let sanitized = val?.toString?.() ?? '';

        // If allowAllCharacters is true, return the value as-is without sanitization
        if (allowAllCharacters) {
            return sanitized;
        }

        if (type === 'number' || allowDecimal || allowNegative) {
            sanitized = sanitized.replace(/[^\d.-]/g, '');

            if (!allowNegative) sanitized = sanitized.replace(/-/g, '');
            if (!allowDecimal) {
                sanitized = sanitized.split('.')[0];
            } else {
                const parts = sanitized.split('.');
                if (parts[1]?.length > numberOfDecimals) {
                    const rounded = Number(sanitized).toFixed(numberOfDecimals);
                    const roundedParts = rounded.split('.');
                    sanitized = `${roundedParts[0]}${roundedParts[1] ? '.' + roundedParts[1].slice(0, numberOfDecimals) : ''}`;
                }
            }
            const numericVal = sanitized.replace('-', '');
            if (!allowZero && numericVal === '0') sanitized = '';
        }

        return sanitized;
    };

    const renderInput = () => {
        if (type === 'textarea') {
            return (
                <textarea
                    id={id}
                    name={name}
                    className={clsx(
                        styles.FormInput,
                        {
                            [styles.Invalid]: !isValid,
                            [styles.HasData]: !isEmpty(value),
                            [styles.WithUnit]: !!unit,
                            [styles.ReadOnly]: readOnly,
                        },
                        inputClassName,
                    )}
                    value={sanitizeValue(value, type, allowDecimal, allowNegative, allowZero, allowAllCharacters)}
                    onChange={handleChange}
                    onKeyUp={(e) => {
                        if (onEnter && e.key === 'Enter') onEnter();
                    }}
                    disabled={disabled}
                    readOnly={readOnly}
                    style={inputStyle}
                    onWheel={(e: any) => e.target?.blur?.()}
                    rows={textAreaRows}
                    placeholder={!showFloatingLabel ? placeholder : undefined}
                />
            );
        }
        return (
            <input
                id={id}
                name={name}
                className={clsx(
                    styles.FormInput,
                    {
                        [styles.Invalid]: !isValid,
                        [styles.HasData]: !isEmpty(value),
                        [styles.WithUnit]: !!unit,
                        [styles.ReadOnly]: readOnly,
                        [styles.Active]: isActive,
                    },
                    inputClassName,
                )}
                type={type}
                pattern={pattern || undefined}
                autoComplete={autoComplete}
                value={sanitizeValue(value, type, allowDecimal, allowNegative, allowZero, allowAllCharacters)}
                onChange={handleChange}
                onKeyUp={(e) => {
                    if (onEnter && e.key === 'Enter') onEnter();
                }}
                onFocus={onFocusProp}
                onBlur={onBlurProp}
                onKeyDown={(e) => {
                    // If allowAllCharacters is true, skip all numeric/format enforcement
                    if (allowAllCharacters) {
                        return;
                    }

                    if (type === 'number') {
                        ['e', 'E', '+'].includes(e.key) && e.preventDefault();
                        if (!allowNegative && e.key === '-') e.preventDefault();
                        if (!allowDecimal && e.key === '.') e.preventDefault();
                        if (!allowZero && e.key === '0' && value === '') e.preventDefault();
                    }
                }}
                onPaste={(e) => {
                    // If allowAllCharacters is true, skip all numeric/format enforcement
                    if (allowAllCharacters) {
                        return;
                    }

                    if (type === 'number') {
                        e.preventDefault();
                        let pasteData = e.clipboardData.getData('text');
                        let sanitized = pasteData.replace(/[^\d.-]/g, '');
                        if (!allowNegative) sanitized = sanitized.replace('-', '');
                        if (!allowDecimal) sanitized = sanitized.split('.')[0];
                        if (!allowZero && sanitized.replace('-', '') === '0') sanitized = '';
                        onChange(sanitized);
                    }
                }}
                disabled={disabled}
                readOnly={readOnly}
                style={inputStyle}
                onWheel={(e: any) => e.target?.blur?.()}
                placeholder={!showFloatingLabel ? placeholder : undefined}
            />
        );
    };

    return (
        <div
            className={clsx(
                styles.InputContainer,
                {
                    [styles.ContainerWithErrorMsg]: !!errorMessage,
                    [styles.TextareaContainer]: type === 'textarea',
                },
                containerClassName,
            )}
            style={style}
            onClick={onClick}
        >
            {renderInput()}
            {showFloatingLabel && (
                <label
                    className={clsx(styles.FormLabel, {
                        [styles.FormLabelInputHasValue]: !isEmpty(value),
                    })}
                    style={labelStyle}
                >
                    {placeholder}
                </label>
            )}
            {unit && (
                <div
                    className={clsx(
                        {
                            [styles.UnitWrap]: true,
                            [styles.UnitWithData]: !isEmpty(value),
                            [styles.Invalid]: !isValid,
                            [styles.Active]: isActive,
                        },
                        unitContainerClassName,
                    )}
                    style={unitContainer}
                >
                    <div style={unitStyle} className={clsx(styles.Unit, unitClassName)}>
                        {unit}
                    </div>
                </div>
            )}
            {!!(!isValid && errorMessage) && <div className={styles.Error}>{errorMessage}</div>}
            {!!(isValid && convertedText) && <div className={styles.ConvertedText}>{convertedText}</div>}
        </div>
    );
}

export default Input;
